import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card } from '../common/Card'
import { Button } from '../common/Button'
import { UnassignedTaskReviewModal } from '../common/UnassignedTaskReviewModal'
import { Task } from '../../lib/types/database.types'

type TaskApprovalScreenProps = {
  isTaskApprover: boolean
  tasks: Task[]
  relatedAppResponsiblePersonMap: Map<string, string | null>
  profileMap: Map<string, string>
  assigneeOptions: Array<{ id: string; label: string }>
  activeTaskId?: string | null
  onApprove: (task: Task, assigneeId: string | null) => Promise<void> | void
  onReject: (task: Task) => Promise<void> | void
  autoOpen?: boolean
  onAutoOpenHandled?: () => void
}

export const TaskApprovalScreen: React.FC<TaskApprovalScreenProps> = ({
  isTaskApprover,
  tasks,
  relatedAppResponsiblePersonMap,
  profileMap,
  assigneeOptions,
  activeTaskId,
  onApprove,
  onReject,
  autoOpen = false,
  onAutoOpenHandled,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedAssigneeByTaskId, setSelectedAssigneeByTaskId] = useState<Record<string, string>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const newRequestCount = tasks.length

  const defaultSelections = useMemo(
    () =>
      Object.fromEntries(
        tasks.map((task) => [
          task.id,
          task.assigned_to || relatedAppResponsiblePersonMap.get(task.related_app_id || '') || '',
        ])
      ),
    [tasks, relatedAppResponsiblePersonMap]
  )

  const openReview = useCallback(() => {
    setSelectedAssigneeByTaskId(defaultSelections)
    setErrorMessage(null)
    setIsOpen(true)
  }, [defaultSelections])

  const closeReview = useCallback(() => {
    setIsOpen(false)
    setSelectedAssigneeByTaskId({})
    setErrorMessage(null)
  }, [])

  useEffect(() => {
    if (!autoOpen || !isTaskApprover) {
      return
    }

    openReview()
    onAutoOpenHandled?.()
  }, [autoOpen, isTaskApprover, onAutoOpenHandled, openReview])

  if (!isTaskApprover) {
    return null
  }

  return (
    <>
      {newRequestCount > 0 ? (
        <Card className="border-l-4 border-amber-500 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-amber-900">
                ⚠️ {newRequestCount} new {newRequestCount === 1 ? 'request' : 'requests'}
              </p>
              <p className="text-sm text-amber-700">New requests waiting for approval</p>
            </div>
            <Button variant="secondary" size="sm" onClick={openReview}>
              View Requests
            </Button>
          </div>
        </Card>
      ) : null}

      {isOpen ? (
        <UnassignedTaskReviewModal
          isOpen={isOpen}
          tasks={tasks}
          onClose={closeReview}
          onApprove={async (task, assigneeId) => {
            if (!assigneeId) {
              setErrorMessage('Assign a user before approving this request.')
              return
            }

            setErrorMessage(null)
            await onApprove(task, assigneeId)
          }}
          onReject={async (task) => {
            setErrorMessage(null)
            await onReject(task)
          }}
          activeTaskId={activeTaskId}
          profileMap={profileMap}
          assigneeOptions={assigneeOptions}
          selectedAssigneeByTaskId={selectedAssigneeByTaskId}
          errorMessage={errorMessage}
          onSelectedAssigneeChange={(taskId, assigneeId) =>
            setSelectedAssigneeByTaskId((current) => ({ ...current, [taskId]: assigneeId }))
          }
        />
      ) : null}
    </>
  )
}
